import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronRight, Search } from "@gaming/xds-react-js-icons";

import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { useRequireAuth } from "../../constants/urlsClientSide";
import {
  formatRelativeTime,
  formatSupportDate,
  getInitials,
  getRecentSupportActivity,
  getSupportCardState,
  getSupportCasePath,
  SupportActivityType,
  SupportCardState,
  supportNewCasePath,
} from "../../helpers/supportCases";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { loadMoreSupportCases, useSupportCases } from "../../helpers/useSupportCases";
import { useStrings } from "../../localization/localizationHelpers";
import { SupportCase } from "../../models/supportCases";
import { getAvatarClass } from "../AdminPage/avatarHelpers";
import { AdminMultiSelectDropdown } from "../AdminPage/components/AdminDropdown";
import * as styles from "./PartnerSupportPage.module.css";

enum ListStatus {
  Submitted = "submitted",
  Active = "active",
  Closed = "closed",
}

const getListStatus = (state: SupportCardState): ListStatus =>
  state === SupportCardState.Closed
    ? ListStatus.Closed
    : state === SupportCardState.Submitted
    ? ListStatus.Submitted
    : ListStatus.Active;

const ownCasesId = "\0own";

/** "First (+N)" trigger text for a multi-select filter. */
const summarize = (
  selected: ReadonlyArray<string>,
  options: ReadonlyArray<Readonly<{ id: string; label: string }>>,
  fallback: string
) => {
  const first = options.find(option => selected.includes(option.id));
  if (!first) {
    return fallback;
  }
  return selected.length > 1 ? `${first.label} (+${selected.length - 1})` : first.label;
};

// Search and filters only see loaded cases. The sentinel remounts after each load,
// so while it stays in view (e.g. a filtered, short list) every page loads in turn.
const observeSentinel = (sentinel: HTMLDivElement | null) => {
  if (!sentinel) {
    return;
  }
  const observer = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting) {
      observer.disconnect();
      loadMoreSupportCases();
    }
  });
  observer.observe(sentinel);
};

export const PartnerSupportPage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.PartnerSettings);
  const isSignedIn = useRequireAuth();
  const history = useHistory();
  const { data, errorStatus, isLoading, isLoadingMore } = useSupportCases(
    isPageReady && isSignedIn
  );
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [creatorFilter, setCreatorFilter] = useState<string[]>([]);
  const [gameFilter, setGameFilter] = useState<string[]>([]);
  const query = search.trim().toLowerCase();

  const [
    title,
    description,
    newTicket,
    searchPlaceholder,
    statusLabel,
    createdByLabel,
    gameLabel,
    byMeLabel,
    clearLabel,
    submittedLabel,
    activeLabel,
    closedLabel,
    submittedOn,
    lastUpdatedOn,
    closedOn,
    newReply,
    awaitingResponse,
    recentActivity,
    activitySubmitted,
    activityReplied,
    activityAttention,
    activityResolved,
    activityClosed,
    noTickets,
    accessDenied,
    loadError,
  ] = useStrings(
    "hubSupportRequestsTitle",
    "hubSupportRequestsDescription",
    "ticketSubmitPageTitle",
    "ticketSearchPlaceholder",
    "status",
    "ticketFilterCreatedByLabel",
    "hubSupportGameLabel",
    "hubSupportCreatedByMe",
    "partnerAdminClearFiltersLabel",
    "submitted",
    "ticketActiveLabel",
    "ticketClosedLabel",
    "hubSupportSubmittedOn",
    "hubSupportLastUpdatedOn",
    "hubSupportClosedOn",
    "hubSupportNewReply",
    "hubSupportAwaitingResponse",
    "hubSupportRecentActivity",
    "hubSupportActivitySubmitted",
    "hubSupportActivityReplied",
    "hubSupportActivityAttention",
    "hubSupportActivityResolved",
    "hubSupportActivityClosed",
    "ticketNoTicketsMsg",
    "ticketAccessDeniedMsg",
    "hubSupportLoadError"
  );

  const cases = (data?.cases ?? []).map(supportCase => {
    const state = getSupportCardState(supportCase);
    return { supportCase, state, listStatus: getListStatus(state) };
  });
  const renderAvatar = ({ createdById, createdByName }: SupportCase) => (
    <span
      className={`${styles.avatar} ${getAvatarClass(createdById ?? createdByName)}`}
      aria-hidden="true"
    >
      {getInitials(createdByName)}
    </span>
  );
  const myCase = cases.find(c => c.supportCase.isOwn)?.supportCase;
  const otherCreators = new Map(
    cases
      .filter(c => !c.supportCase.isOwn && c.supportCase.createdByName)
      .map(c => [c.supportCase.createdByName, c.supportCase] as const)
  );
  const creatorOptions = [
    { id: ownCasesId, label: byMeLabel, icon: myCase && renderAvatar(myCase) },
    ...Array.from(otherCreators.keys())
      .sort()
      .map(name => ({
        id: name,
        label: name,
        icon: renderAvatar(otherCreators.get(name) as SupportCase),
      })),
  ];
  const gameOptions = Array.from(
    new Map(
      cases.flatMap(({ supportCase: { gameConceptId, gameName } }) =>
        gameConceptId && gameName ? [[gameConceptId, gameName] as const] : []
      )
    ),
    ([id, label]) => ({ id, label })
  ).sort((a, b) => a.label.localeCompare(b.label));
  const activity = getRecentSupportActivity(data?.cases ?? []);

  const visibleCases = cases.filter(
    ({ supportCase, listStatus }) =>
      (!statusFilter.length || statusFilter.includes(listStatus)) &&
      (!creatorFilter.length ||
        creatorFilter.includes(supportCase.isOwn ? ownCasesId : supportCase.createdByName)) &&
      (!gameFilter.length || gameFilter.includes(supportCase.gameConceptId ?? "")) &&
      (!query ||
        supportCase.title.toLowerCase().includes(query) ||
        supportCase.ticketNumber.toLowerCase().includes(query))
  );

  const statusLabels: Record<ListStatus, string> = {
    [ListStatus.Submitted]: submittedLabel,
    [ListStatus.Active]: activeLabel,
    [ListStatus.Closed]: closedLabel,
  };
  const statusOptions = Object.values(ListStatus).map(id => ({ id, label: statusLabels[id] }));
  const filters = [
    { label: statusLabel, options: statusOptions, values: statusFilter, onChange: setStatusFilter },
    data?.scope === "all" && {
      label: createdByLabel,
      options: creatorOptions,
      values: creatorFilter,
      onChange: setCreatorFilter,
    },
    gameOptions.length > 1 && {
      label: gameLabel,
      options: gameOptions,
      values: gameFilter,
      onChange: setGameFilter,
    },
  ];
  const activityLabels: Record<SupportActivityType, string> = {
    [SupportActivityType.Submitted]: activitySubmitted,
    [SupportActivityType.Replied]: activityReplied,
    [SupportActivityType.Attention]: activityAttention,
    [SupportActivityType.Resolved]: activityResolved,
    [SupportActivityType.Closed]: activityClosed,
  };

  const getDateLabel = (supportCase: SupportCase, state: SupportCardState) =>
    state === SupportCardState.Closed
      ? closedOn.replace("{0}", formatSupportDate(supportCase.modifiedOn))
      : state === SupportCardState.Submitted
      ? submittedOn.replace("{0}", formatSupportDate(supportCase.createdOn))
      : lastUpdatedOn.replace("{0}", formatSupportDate(supportCase.modifiedOn));

  const renderBody = () => {
    if (!isPageReady || !isSignedIn || isLoading) {
      return <PageLoadingSpinner />;
    }
    if (errorStatus !== undefined) {
      return (
        <p role="alert" className={styles.message}>
          {errorStatus === 403 ? accessDenied : loadError}
        </p>
      );
    }
    return (
      <div className={styles.columns}>
        <div>
          <div className={styles.toolbar}>
            <label className={styles.search}>
              <Icon aria-hidden="true" icon={Search} iconSize={IconSize.Custom} />
              <input
                type="search"
                value={search}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            {filters.map(
              filter =>
                filter && (
                  <AdminMultiSelectDropdown
                    key={filter.label}
                    ariaLabel={filter.label}
                    className={filter.values.length ? styles.activeFilter : undefined}
                    clearLabel={clearLabel}
                    displayLabel={summarize(filter.values, filter.options, filter.label)}
                    label={filter.label}
                    options={filter.options}
                    values={filter.values}
                    onChange={filter.onChange}
                  />
                )
            )}
          </div>
          {visibleCases.length === 0 && !data?.hasMore ? (
            <p className={styles.message}>{noTickets}</p>
          ) : (
            <ul className={styles.caseList}>
              {visibleCases.map(({ supportCase, listStatus, state }) => {
                const footer =
                  state === SupportCardState.NewReply
                    ? newReply
                    : state === SupportCardState.AwaitingPartner
                    ? awaitingResponse
                    : undefined;
                return (
                  <li key={supportCase.caseId}>
                    <Link
                      to={getSupportCasePath(supportCase.caseId)}
                      className={`${styles.caseCard} ${
                        state === SupportCardState.Closed ? styles.closedCard : ""
                      }`}
                      data-testid="support-case-card"
                    >
                      <div className={styles.caseBody}>
                        <div className={styles.caseText}>
                          <span className={styles.caseNumber}>{supportCase.ticketNumber}</span>
                          <span className={styles.caseTitle}>{supportCase.title}</span>
                          <span className={styles.caseMeta}>
                            {renderAvatar(supportCase)}
                            {supportCase.createdByName} · {getDateLabel(supportCase, state)}
                          </span>
                        </div>
                        <span className={`${styles.status} ${styles[listStatus]}`}>
                          {statusLabels[listStatus]}
                        </span>
                        <Icon
                          aria-hidden="true"
                          className={styles.chevron}
                          icon={ChevronRight}
                          iconSize={IconSize.Custom}
                        />
                      </div>
                      {footer && (
                        <div
                          className={`${styles.caseFooter} ${
                            state === SupportCardState.NewReply ? styles.newReply : styles.awaiting
                          }`}
                        >
                          <span className={styles.footerLabel}>{footer}</span>
                          {supportCase.latestMessagePreview && (
                            <span className={styles.preview}>
                              {supportCase.latestMessagePreview}
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {data?.hasMore &&
            (isLoadingMore ? (
              <div className={styles.loadingMore}>
                <XboxSpinner size={48} />
              </div>
            ) : (
              <div ref={observeSentinel} className={styles.loadingMore} />
            ))}
        </div>
        <aside className={styles.activity} aria-labelledby="support-activity-title">
          <h2 id="support-activity-title">{recentActivity}</h2>
          <ul>
            {activity.map(item => (
              <li key={`${item.type}-${item.supportCase.caseId}`}>
                <Link to={getSupportCasePath(item.supportCase.caseId)}>
                  {activityLabels[item.type].replace("{0}", item.supportCase.ticketNumber)}
                </Link>
                <span className={styles.activityTime}>{formatRelativeTime(item.on)}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    );
  };

  return (
    <PartnerPageLayout breadcrumbLabel={title} wide>
      <header className={styles.header}>
        <div>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {errorStatus !== 403 && (
          <BasicButton
            as="a"
            buttonType={ButtonType.Brand}
            href={supportNewCasePath}
            onClick={event => {
              event.preventDefault();
              history.push(supportNewCasePath);
            }}
          >
            {newTicket}
          </BasicButton>
        )}
      </header>
      {renderBody()}
    </PartnerPageLayout>
  );
};
